import { ScheduleDisableReason } from '@classpass/protobuf-definitions/lib/node/models/inventory/v1/schedule_configuration';
import { Schedules } from '@classpass/protobuf-definitions/lib/node/models/partners/schedule';
import { addDays, differenceInMinutes, endOfDay, isBefore, isPast, isWithinInterval, parse } from 'date-fns';

import { CLASS_CANCELLED_SCHEDULE_DISABLED_REASON, CLASS_ENTITY_TYPE, PERMISSION } from 'constants/logic';
import URLS from 'constants/urls';
import { transformClientResponse, transformKeysToCamelCase, userHasPermission } from 'helpers/core';
import { httpClient } from 'helpers/http';
import { adjustDateForTimezone, parseDateInTimezone } from 'helpers/intl-fns';

const LIST_PRESENTATION_FIELD = 'list_presentation';

const getVenueOpportunities = async venueId => {
  const response = await httpClient.get(URLS.venue.opportunities(venueId));
  return transformClientResponse(response);
};

const attachListPresentation = (parsed, rawResponse) => {
  const rawSchedules = rawResponse?.schedules;
  if (!Array.isArray(rawSchedules) || !parsed?.schedules) {
    return parsed;
  }

  const presentationByScheduleId = new Map();
  rawSchedules.forEach(rawSchedule => {
    if (!rawSchedule?.[LIST_PRESENTATION_FIELD]) {
      return;
    }

    presentationByScheduleId.set(
      String(rawSchedule.schedule_id),
      transformKeysToCamelCase(rawSchedule[LIST_PRESENTATION_FIELD])
    );
  });

  if (presentationByScheduleId.size === 0) {
    return parsed;
  }

  return {
    ...parsed,
    schedules: parsed.schedules.map(schedule => {
      const listPresentation = presentationByScheduleId.get(String(schedule.scheduleId));
      return listPresentation ? { ...schedule, listPresentation } : schedule;
    }),
  };
};

export const getVenueSchedules = async (date, page, entityType, venueId, includeListPresentation = false) => {
  const response = await httpClient.get(URLS.venue.schedules(venueId, date, page, entityType, includeListPresentation));
  return attachListPresentation(transformKeysToCamelCase(Schedules.fromJSON(response.data)), response.data);
};

export const findActiveSchedule = (schedules, activeScheduleId) => {
  return schedules?.find(({ scheduleId }) => scheduleId === activeScheduleId) || {};
};

export const findNextUpcomingSchedule = (schedules = [], venueTimezone, now = new Date()) => {
  // startDateTime is the venue's wall-clock time parsed as a local Date, so "now" has to be
  // shifted into the venue timezone before the two can be compared.
  const venueNow = venueTimezone ? adjustDateForTimezone(now, venueTimezone) : now;

  return schedules.reduce((nextSchedule, schedule) => {
    const { startDateTime } = schedule;

    if (!startDateTime || isBefore(startDateTime, venueNow)) {
      return nextSchedule;
    }

    return !nextSchedule || isBefore(startDateTime, nextSchedule.startDateTime) ? schedule : nextSchedule;
  }, null);
};

export const resolveScheduleFilterCounts = (...filterCountsCandidates) =>
  filterCountsCandidates.find(filterCounts => filterCounts != null);

export const fetchVenueSchedulesUntilCurrentTime = async (
  date,
  page = 0,
  entityType = null,
  venueId,
  venueTimezone,
  includeListPresentation = false
) => {
  const allSchedules = [];
  let filterCounts;
  let latestPagination = {};
  let currentPage = page;
  let totalPages = 1;

  // We fetch pages of schedules recursively until we reach the final schedule or the last schedule's start time is not in the past
  const fetchMoreSchedules = async () => {
    const venueSchedulesResponse = await getVenueSchedules(
      date,
      currentPage,
      entityType,
      venueId,
      includeListPresentation
    );
    const { filterCounts: pageFilterCounts, pagination = {}, schedules = [] } = venueSchedulesResponse;
    const { totalPages: total } = pagination;

    filterCounts = resolveScheduleFilterCounts(filterCounts, pageFilterCounts);

    if (schedules.length === 0) {
      return;
    }

    allSchedules.push(...schedules);
    latestPagination = pagination;
    totalPages = total;

    const lastSchedule = allSchedules[allSchedules.length - 1];
    const { startDate, endTime, startTime } = lastSchedule;
    const startDateTime = parse(`${startDate} ${startTime}`, 'yyyy-MM-dd HH:mm', new Date());
    let endDateTime = parse(`${startDate} ${endTime}`, 'yyyy-MM-dd HH:mm', new Date());
    let lastScheduleEndTimeInTz = parseDateInTimezone(`${startDate} ${endTime}`, venueTimezone);

    // account for classes that cross the date-boundary
    if (isBefore(endDateTime, startDateTime)) {
      endDateTime = addDays(endDateTime, 1);
      lastScheduleEndTimeInTz = addDays(lastScheduleEndTimeInTz, 1);
    }
    const isStartDateInPast = isPast(endOfDay(parse(date, 'yyyy-MM-dd', new Date())));

    currentPage += 1;
    // We want to request more schedules until the last schedule's end time is in the past
    // only if the current date is not in the past, i.e., the current date is today or in the future.
    if (currentPage < totalPages && isPast(lastScheduleEndTimeInTz) && !isStartDateInPast) {
      await fetchMoreSchedules();
    }
  };

  await fetchMoreSchedules();
  return { filterCounts, pagination: latestPagination, schedules: allSchedules };
};

export const getOpportunitiesMap = async (user, venueId) => {
  let opportunitiesMap = {};
  if (userHasPermission(user, PERMISSION.DISPLAY_SPOT_MANAGEMENT)) {
    try {
      const { opportunities } = await getVenueOpportunities(venueId);
      opportunitiesMap = opportunities.reduce((acc, opportunity) => {
        const { scheduleId } = opportunity;
        acc[scheduleId] = opportunity;
        return acc;
      }, {});
    } catch (error) {
      reportError('Error fetching opportunities for schedules', error);
    }
  }
  return opportunitiesMap;
};

export const enrichSchedulesWithOpportunities = async (opportunitiesMap, schedules) => {
  return schedules.map(schedule => {
    const { scheduleId } = schedule;
    return {
      ...schedule,
      opportunity: opportunitiesMap[scheduleId],
    };
  });
};

export const mapDerivedSchedulesData = (schedules, venue, venueSmartspotData) => {
  const { directFillDataDisabled, timezone: venueTimezone, venueId, isManualStudio } = venue;

  return schedules.map(schedule => {
    const {
      apiNoCapacity,
      disabledReason: scheduleDisabledReasonRaw,
      endTime,
      scheduleClass = {},
      scheduleDisabled,
      scheduleId,
      spots = {},
      startDate,
      startTime,
    } = schedule;
    const { classDisabled, entityType } = scheduleClass;

    const scheduleDisabledReason = ScheduleDisableReason[scheduleDisabledReasonRaw] ?? scheduleDisabledReasonRaw;

    // disabledReason comes from an append-only log that survives re-enables, so a stale cancel
    // entry must be ignored unless the schedule is currently disabled at the schedule level
    const isCancelled =
      Boolean(scheduleDisabled) && CLASS_CANCELLED_SCHEDULE_DISABLED_REASON.includes(scheduleDisabledReason);

    const startDateTime = parse(`${startDate} ${startTime}`, 'yyyy-MM-dd HH:mm', new Date());
    const startDateTimeInTz = parseDateInTimezone(`${startDate} ${startTime}`, venueTimezone);
    let endDateTime = parse(`${startDate} ${endTime}`, 'yyyy-MM-dd HH:mm', new Date());
    let endDateTimeInTz = parseDateInTimezone(`${startDate} ${endTime}`, venueTimezone);

    // account for classes that cross the date-boundary
    if (isBefore(endDateTime, startDateTime)) {
      endDateTime = addDays(endDateTime, 1);
      endDateTimeInTz = addDays(endDateTimeInTz, 1);
    }
    const now = new Date();
    const isInProgress = isWithinInterval(now, { start: startDateTimeInTz, end: endDateTimeInTz });
    const isInThePast = isPast(endDateTimeInTz);

    const isWellness = entityType === CLASS_ENTITY_TYPE.APPOINTMENT;

    const { isEnabled: smartspotEnabledForVenue, scheduleData: smartspotScheduleData } = venueSmartspotData;
    const smartspotDataForSchedule = smartspotScheduleData[scheduleId] || {};
    const {
      isEnabled: smartspotEnabled,
      mode: smartspotMode,
      networkSpotCap: smartspotNetworkSpotCap,
      reason: smartspotReason,
    } = smartspotDataForSchedule || {};

    const { classpassTotal, total, booked: totalBooked, takenSpots } = spots;
    const maxCapacity = total === 0 ? null : total;
    const hasIntegrationData = !isManualStudio && !directFillDataDisabled && maxCapacity !== null;
    // For integrated studios, noCapacity means CORE received a signal that the class was full.
    // There's no guarantee that all full classes for all integrated partners will have received that signal.
    // We assume that integrations that send the noCapacity signal will signal again if spots become available.
    // For manual studios, noCapacity is used as a reservations e-brake.
    const reservationsStopped = isManualStudio && apiNoCapacity;
    // We can never know if a manual studio is completely sold out.
    // >= because studios can overbook themselves
    const classFull = !isManualStudio && (apiNoCapacity || totalBooked >= maxCapacity);
    // "0/0" and "10/10" are both "full" but we want to treat "0/0" differently
    const classpassSpotsUnfilled = classpassTotal === 0 ? Infinity : classpassTotal - takenSpots;
    const classpassSoldOut =
      classpassTotal > 0 && classpassSpotsUnfilled === 0 && !classFull && !reservationsStopped && !smartspotEnabled;
    const classpassAlmostSoldOut =
      classpassTotal > 0 &&
      takenSpots > 0 &&
      classpassSpotsUnfilled > 0 &&
      classpassSpotsUnfilled <= 3 &&
      !classFull &&
      !reservationsStopped &&
      !smartspotEnabled;

    return {
      ...schedule,
      scheduleName: schedule?.scheduleName || schedule?.scheduleClass?.className,
      spots: {
        ...spots,
        maxCapacity,
        totalBooked,
      },
      classpassAlmostSoldOut,
      classpassSoldOut,
      classpassSpotsUnfilled,
      duration: differenceInMinutes(endDateTime, startDateTime),
      endDateTime,
      hasIntegrationData,
      isCancelled,
      isDisabled: classDisabled || scheduleDisabled,
      isFull: classFull,
      isInProgress,
      isInThePast,
      isLocked: classDisabled || scheduleDisabled || isCancelled || isInThePast,
      isManualStudio,
      isWellness,
      reservationsStopped,
      smartspotEnabledForVenue,
      // optionValue defaults to null but will be 'on' if it was ever toggled
      smartspotEnabled,
      smartspotReason,
      smartspotMode,
      smartspotNetworkSpotCap,
      startDateTime,
      venueId,
      venueTimezone,
    };
  });
};
